import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import {
  canonicalPathname,
  DEFAULT_META,
  DEFAULT_OG_IMAGE,
  lookupKey,
  NOT_FOUND_META,
  ROUTE_META,
  SITE_NAME,
  SITE_URL,
} from './routeMeta';

// Applies per-route <title>, description, canonical, robots, and Open Graph
// tags on navigation. Written as a small hook rather than pulling in
// react-helmet so the dependency tree stays unchanged.
//
// Note: this runs client-side. Crawlers that execute JavaScript (Google,
// Bing) will see these values; crawlers that don't will see the defaults
// baked into public/index.html. That's an accepted trade-off until/unless
// the app moves to server rendering.

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);

  if (!el) {
    el = document.createElement('meta');
    Object.keys(attrs).forEach((key) => {
      if (key !== 'content') el.setAttribute(key, attrs[key]);
    });
    document.head.appendChild(el);
  }

  el.setAttribute('content', attrs.content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);

  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }

  el.setAttribute('href', href);
  return el;
}

function resolveMeta(pathname) {
  // Match slash-insensitively. React Router ignores trailing slashes when
  // matching routes, and the S3 website origin actively redirects paths INTO
  // the trailing-slash form — so `/about/` is the URL users and crawlers
  // actually land on. Looking up the raw pathname would miss `/about` and fall
  // through to the not-found metadata, which would noindex every real page.
  const key = lookupKey(pathname);

  if (ROUTE_META[key]) {
    return { meta: ROUTE_META[key], pattern: key };
  }

  const pattern = Object.keys(ROUTE_META).find(
    (candidate) => candidate.includes(':') && matchPath(candidate, key)
  );

  if (pattern) return { meta: ROUTE_META[pattern], pattern };

  // No route matched — this is the catch-all page.
  return { meta: NOT_FOUND_META, pattern: null };
}

export default function DocumentMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const { meta } = resolveMeta(pathname);

    const title = meta.title || DEFAULT_META.title;
    const description = meta.description || DEFAULT_META.description;
    const canonical = `${SITE_URL}${canonicalPathname(
      meta.canonicalPath || pathname
    )}`;

    document.title = title;

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: description,
    });

    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: meta.noindex ? 'noindex, follow' : 'index, follow',
    });

    upsertLink('canonical', canonical);

    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website',
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: SITE_NAME,
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonical,
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    });

    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description,
    });
  }, [pathname]);

  return null;
}
